import { ReactNode } from "react";
import { cx } from "shared/utils";
import { useInboxEmbed } from "../context/useInboxEmbed";

const BUBBLE_MAX_WIDTH = 580;
const EMAIL_MAX_WIDTH = 900;
const CARD_PADDING = 20;
const CARD_BORDER = 1;
const CARD_CHROME = (CARD_PADDING + CARD_BORDER) * 2;
// Enough for the From/To lines, which an email narrower than them would
// otherwise wrap.
const EMAIL_MIN_WIDTH = 360;

interface MessageContentCardProps {
  isFromContact: boolean;
  children: ReactNode;
  className?: string;
  /** Canvas the email declares; the card sizes to it. 0 or absent if none. */
  contentWidth?: number;
  /** Outgoing email/SMS bubbles carry the design's light pink ground. */
  tinted?: boolean;
  usesInboundColor?: boolean;
}

export const MessageContentCard = ({
  isFromContact,
  children,
  className,
  contentWidth,
  tinted = true,
  usesInboundColor
}: MessageContentCardProps) => {
  const inboundColor = usesInboundColor ?? isFromContact;
  // An iframe body is width:100%, which adds nothing to a shrink-to-fit box —
  // so left alone the card would size itself to the subject line instead.
  // Every bubble carries the design's 16px radius; the pop-up tightens the
  // padding to 16px against the wider card /inbox has always drawn.
  const isCompact = Boolean(useInboxEmbed()?.compact);

  const emailWidth = contentWidth
    ? Math.min(
        Math.max(contentWidth + CARD_CHROME, EMAIL_MIN_WIDTH),
        EMAIL_MAX_WIDTH
      )
    : undefined;

  return (
    <div
      className={cx(
        "rounded-2xl border border-alpha-80 shadow-xs",
        tinted && !inboundColor ? "bg-primary-20" : "bg-content-1000-inverted",
        isCompact ? "p-4" : "p-5",
        isFromContact ? "rounded-tl-none" : "rounded-tr-none",
        className
      )}
      style={{
        maxWidth: `min(${emailWidth ?? BUBBLE_MAX_WIDTH}px, 100%)`,
        width: emailWidth
      }}
    >
      {children}
    </div>
  );
};
